import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Meta } from '@/components/common';
import { LinkUnavailable } from '@/components/features/review/LinkUnavailable';
import { PasscodeGate } from '@/components/features/review/PasscodeGate';
import { ReviewWorkspace } from '@/components/features/review/ReviewWorkspace';
import { ApiError } from '@/services/api/client';
import { reviewService } from '@/services/api/review.service';
import { useDecide, useOpenLink } from '@/services/queries';
import { queryKeys } from '@/services/queries/keys';
import { Logo } from '@/shared/ui/logo';
import { Spinner } from '@/shared/ui/spinner';
import {
  layersOf,
  marginsOf,
  parametersOf,
  useDesignMetadata,
  type DesignMetadata,
} from '@/lib/live-design';
import type {
  CaseSummary,
  DecisionView,
  NoteView,
  PatientLinkResponse,
  PatientLinkView,
  ReviewCase,
  SceneManifest,
  ShareOpenResponse,
  ViewState,
} from '@/types';

/**
 * The dentist's review link: the review workspace, fed from the share token.
 *
 * Everything comes from three reads - /open (the design, its notes and any
 * decision), /case (the header) and the design's metadata document (what each
 * part of the GLB is, and the tooth) - and the dentist's answer goes back
 * through /decision: the decision with the notes they added, as one round.
 * The token in the path is the only credential.
 *
 * The patient link (a view-only copy for the dentist's patient, with their
 * answer about the fitting date) runs on /patient-link with the same token.
 *
 * The PDF report button shows when the lab has built a 3D PDF for the case
 * (/report). Each click asks for a fresh signed link, which lasts an hour.
 */

type Bundle = NonNullable<ShareOpenResponse['bundle']>;

function useCaseHeader(token: string) {
  return useQuery({
    queryKey: [...queryKeys.review.open(token), 'case'],
    enabled: token.length > 0,
    staleTime: Infinity,
    queryFn: ({ signal }) => reviewService.case(token, signal),
  });
}

/** Whether the lab has built a 3D PDF for this case. 404 means not yet: no button. */
function useHasReport(token: string, enabled: boolean) {
  return useQuery({
    queryKey: [...queryKeys.review.open(token), 'report'],
    enabled: enabled && token.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: false,
    queryFn: async ({ signal }) => {
      try {
        await reviewService.report(token, signal);
        return true;
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return false;
        throw err;
      }
    },
  });
}

function summaryOf(
  bundle: Bundle,
  expiresAt: string,
  metadata: DesignMetadata | undefined,
  header: ReviewCase | undefined,
): CaseSummary {
  return {
    labName: header?.labName ?? 'Your lab',
    patientRef: header?.reference ?? '—',
    // A link with several crowns lists each tooth; they share the case's
    // material and shade.
    teeth: (metadata?.teeth?.length ? metadata.teeth : [metadata?.tooth ?? '—']).map((tooth) => ({
      tooth,
      restoration: 'Crown',
      material: metadata?.material ?? header?.material ?? '—',
      shade: metadata?.shade ?? null,
    })),
    version: bundle.version,
    designedBy: header?.designedBy ?? '—',
    designedAt: header?.createdAt ?? new Date().toISOString(),
    expiresAt,
    // An older design's metadata has none; the Details tab then leaves the section out.
    parameters: parametersOf(metadata),
    designerNote: null,
  };
}

function notesOf(notes: ShareOpenResponse['notes']): NoteView[] {
  let pin = 0;
  return notes.map((note) => {
    const position = note.position;
    const view =
      note.cameraState && note.layerState
        ? ({ camera: note.cameraState, layers: note.layerState } as ViewState)
        : null;
    return {
      id: note.id,
      pin: position ? ++pin : null,
      authorName: note.authorName,
      authorRole: 'dentist',
      text: note.text,
      createdAt: note.createdAt,
      position: position ? [position.x, position.y, position.z] : null,
      view,
      stage: note.stage ?? null,
    };
  });
}

function patientLinkOf(link: PatientLinkResponse | undefined): PatientLinkView | null {
  // A link made before its token was kept has no URL to show; treat it as none.
  if (!link?.url) return null;
  return { ...link, url: link.url };
}

function decisionOf(decision: ShareOpenResponse['decision']): DecisionView | null {
  if (!decision) return null;
  return {
    decision: decision.decision,
    deciderName: decision.deciderName,
    clinic: decision.deciderClinic ?? '',
    registrationNo: decision.deciderRegNo ?? '',
    note: decision.note ?? null,
    decidedAt: decision.signedAt,
  };
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-background px-4 text-center">
      <Logo />
      {children}
    </div>
  );
}

export function ReviewPage() {
  const { token = '' } = useParams<{ token: string }>();
  const [passcode, setPasscode] = useState<string | undefined>();

  const link = useOpenLink(token, passcode);
  const bundle = link.data?.bundle ?? null;
  const metadata = useDesignMetadata(queryKeys.review.open(token), bundle?.metadataUrl);
  const header = useCaseHeader(token);
  const hasReport = useHasReport(token, Boolean(link.data));
  const decide = useDecide(token);
  const queryClient = useQueryClient();
  const patientLink = useQuery({
    queryKey: queryKeys.review.patientLink(token),
    enabled: Boolean(link.data),
    queryFn: ({ signal }) => reviewService.patientLink(token, signal),
    // The patient answers on their own phone, so while a fitting date waits
    // for them the dentist's page asks again; their answer shows without a
    // reload. Stops once they have answered or the link is off.
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.active && data.appointment?.status === 'pending' ? 15_000 : false;
    },
    refetchOnWindowFocus: true,
  });
  const setPatientLink = (value: PatientLinkResponse) => {
    queryClient.setQueryData(queryKeys.review.patientLink(token), value);
  };
  const createPatientLink = useMutation({
    mutationFn: (body: Parameters<typeof reviewService.createPatientLink>[1]) =>
      reviewService.createPatientLink(token, body),
    onSuccess: setPatientLink,
  });
  const turnOffPatientLink = useMutation({
    mutationFn: () => reviewService.turnOffPatientLink(token),
    onSuccess: setPatientLink,
  });

  const manifest = useMemo<SceneManifest | null>(
    () =>
      bundle && !metadata.isPending
        ? {
            source: { type: 'glb', url: bundle.glbUrl },
            layers: layersOf(metadata.data),
            margins: marginsOf(metadata.data),
          }
        : null,
    [bundle, metadata.isPending, metadata.data],
  );

  const error = link.error instanceof ApiError ? link.error : null;
  if (error?.isDead) return <LinkUnavailable code={error.code} />;
  if (error?.needsPasscode) {
    return (
      <PasscodeGate
        pending={link.isFetching}
        error={error.code === 'passcode_invalid' ? error.message : undefined}
        onSubmit={(value) => {
          setPasscode(value);
        }}
      />
    );
  }

  if (link.isPending || (bundle && !manifest)) {
    return (
      <Centered>
        <Spinner />
        <p className="text-sm text-muted-foreground">Opening the design…</p>
      </Centered>
    );
  }

  if (link.isError || !link.data) {
    return (
      <Centered>
        <p className="max-w-sm text-sm text-muted-foreground">
          {error?.message ?? 'Something went wrong opening this design. Try reloading the page.'}
        </p>
        {error?.requestId && (
          <p className="font-mono text-xs text-muted-foreground">Reference: {error.requestId}</p>
        )}
      </Centered>
    );
  }

  if (!bundle || !manifest) {
    return (
      <Centered>
        <p className="max-w-sm text-sm text-muted-foreground">
          The design is still being prepared. Reload this page in a moment.
        </p>
      </Centered>
    );
  }

  return (
    <>
      <Meta title="Crown design for review" />
      <ReviewWorkspace
        summary={summaryOf(bundle, link.data.expiresAt, metadata.data, header.data)}
        manifest={manifest}
        notes={notesOf(link.data.notes)}
        decision={decisionOf(link.data.decision)}
        knownDentist={
          metadata.data?.dentist?.name || metadata.data?.dentist?.clinic
            ? {
                name: metadata.data.dentist.name ?? '',
                clinic: metadata.data.dentist.clinic ?? '',
                registrationNo: '',
              }
            : null
        }
        storageKey={bundle.id}
        {...(hasReport.data
          ? {
              onReport: () => {
                // Opened now, inside the click, so the browser does not treat
                // it as a pop-up; pointed at the PDF once the link arrives.
                const tab = window.open('', '_blank');
                reviewService
                  .report(token)
                  .then((report) => {
                    if (tab) tab.location.href = report.url;
                    else window.location.assign(report.url);
                  })
                  .catch(() => {
                    tab?.close();
                    toast.error('The PDF report could not be opened. Try again in a moment.');
                  });
              },
            }
          : {})}
        onDecide={async (input) => {
          await decide.mutateAsync({
            decision: input.decision,
            deciderName: input.deciderName,
            ...(input.clinic ? { deciderClinic: input.clinic } : {}),
            ...(input.registrationNo ? { deciderRegNo: input.registrationNo } : {}),
            ...(input.note ? { note: input.note } : {}),
            notes: input.notes.map((note) => ({
              text: note.text,
              ...(note.stage ? { stage: note.stage } : {}),
              ...(note.position
                ? { position: { x: note.position[0], y: note.position[1], z: note.position[2] } }
                : {}),
              ...(note.view ? { cameraState: note.view.camera, layerState: note.view.layers } : {}),
            })),
          });
        }}
        patientLink={patientLinkOf(patientLink.data)}
        onCreatePatientLink={async (request) => {
          await createPatientLink.mutateAsync({
            email: request.email,
            message: request.message,
            language: request.language as 'en',
            appointmentAt: request.appointmentAt,
            from: link.data.decision?.deciderName ?? link.data.notes.at(-1)?.authorName ?? null,
          });
        }}
        onTurnOffPatientLink={async () => {
          await turnOffPatientLink.mutateAsync();
        }}
      />
    </>
  );
}
